// eslint-disable-line no-unused-vars
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
// eslint-disable-line
import React, { useEffect, useRef, useState } from 'react';
import { FaCog } from 'react-icons/fa';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { t } from 'ttag';

import { MAP_LAYERS, MAP_LAYER_TYPES } from '../../constants/map-layers';
import Spinner from '../../media/spinner.svg';
import { FILTERS } from '../Field/constants/filters';
import { ButtonPrimarySmall } from '../Shared/Button/Button';
import Filter from '../Shared/Filter/Filter';
import { FETCH_STATUSES } from './constants/consts';
import { Map, MapFilterPanel, SearchNewFieldsPanel } from './fields.styles';
import MapLayerCard from './MapLayerCard';

import 'leaflet/dist/leaflet.css';
//eslint-disable-next-line
import maplibreGL from '@maplibre/maplibre-gl-leaflet';
import useSoilTypes from '../../hooks/useSoilTypes';
import useCropTypes from '../../hooks/useCropTypes';

const LEAFLET_PANES = {
  fields: 'fields',
};

export default function FieldsMap({
  onMoveEnd,
  fields,
  scope,
  onSearchClick,
  onOutsideFilterClick,
  onToggleFilterClick,
  onFilterCloseClick,
  mapLayer,
  fieldsStatus,
  activeFilter,
  onFilterChangeClick,
  bbox,
  wasUserDrag,
  zoom,
  onZoomChange,
}) {
  const [hasDraggedMap, setHasDraggedMap] = useState(false);
  const mapRef = useRef();
  const navigate = useNavigate();
  const leafletMap = useRef();
  const fieldsGroupRef = useRef();
  const theme = useTheme();
  const { search } = useLocation();

  const MIN_ZOOM_LEVEL_TO_SEARCH_FOR_FIELDS = 14;

  const { getSoilTypeNameById } = useSoilTypes(scope);
  const { getCropNameById } = useCropTypes(scope);

  useEffect(() => {
    async function addMapLayersToMap() {
      leafletMap.current = L.map(mapRef.current, {
        doubleClickZoom: false,
      });
      MAP_LAYERS.forEach((layer) => {
        leafletMap.current.createPane(layer.SATFARM_INTERNAL_ID);
        const pane = leafletMap.current.createPane(layer.id);
        if (layer.type === MAP_LAYER_TYPES.mapLibre) {
          L.maplibreGL({
            ...layer.settings,
            pane,
          }).addTo(leafletMap.current);
        }

        if (layer.type === MAP_LAYER_TYPES.tileLayer) {
          L.tileLayer.wms(layer.url, { ...layer.settings, pane }).addTo(leafletMap.current);
        }
      });

      const { _southWest, _northEast } = bbox;

      const newBBox = [
        [_southWest.lat, _southWest.lng],
        [_northEast.lat, _northEast.lng],
      ];

      const newbbox = L.latLngBounds(newBBox);
      leafletMap.current.fitBounds(newbbox);
      leafletMap.current.on('zoomend', () => {
        setHasDraggedMap(true);
        onMoveEnd(leafletMap.current.getBounds());
        onZoomChange(leafletMap.current.getZoom());
      });
      leafletMap.current.on('dragend', () => {
        setHasDraggedMap(true);

        onMoveEnd(leafletMap.current.getBounds());
      });
    }
    addMapLayersToMap();
    return () => leafletMap.current.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!wasUserDrag) {
      const { _southWest, _northEast } = bbox;
      const latLngNorthEast = L.latLng(_northEast.lat, _northEast.lng);
      const latLngSouthWest = L.latLng(_southWest.lat, _southWest.lng);
      const newbbox = L.latLngBounds([latLngNorthEast, latLngSouthWest]);
      leafletMap.current.fitBounds(newbbox);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bbox]);

  useEffect(() => {
    if (fieldsGroupRef.current && mapRef.current) {
      leafletMap.current.removeLayer(fieldsGroupRef.current);
    }
    fieldsGroupRef.current = L.layerGroup().addTo(leafletMap.current);

    fields.forEach((field) => addFieldToMap(field, fieldsGroupRef.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  function addFieldToMap(field, layerGroup) {
    const fieldStyle = {
      color: theme.fieldColor,
      weight: theme.fieldWeight,
      opacity: theme.fieldOpacity,
    };

    leafletMap.current.createPane(LEAFLET_PANES.fields);
    const fieldLink = document.createElement('div');
    fieldLink.innerText = t`Analyse field`;
    fieldLink.style.height = theme.formElementBaseHeight;
    fieldLink.style.background = theme.colorPrimary50;
    fieldLink.style.display = 'flex';
    fieldLink.style.alignItems = 'center';
    fieldLink.style.fontSize = theme.fontSize03;
    fieldLink.style.fontWeight = theme.fontWeight;
    fieldLink.style.borderRadius = theme.borderRadius;
    fieldLink.style.color = 'white';
    fieldLink.style.cursor = 'pointer';
    fieldLink.style.padding = `0 ${theme.spacing02}`;
    fieldLink.addEventListener('click', () => navigate(`/${field.id}${search}`));

    L.geoJson(field.geometry, {
      pane: LEAFLET_PANES.fields,
      onEachFeature: function (f, l) {
        l.options.SATFARM_INTERNAL_ID = field.id;
        l.bindPopup(fieldLink);
      },
      style: fieldStyle,
    })
      .bindTooltip(() => {
        const cropName = getCropNameById(field.cropId);
        const soilTypeName = getSoilTypeNameById(field.soilTypeId);

        return `${t`Crop type`}: ${cropName}<br> ${t`Soil type`}: ${soilTypeName}`;
      })
      .addTo(layerGroup);
  }

  useEffect(() => {
    if (!leafletMap.current) {
      return;
    }

    leafletMap.current.eachLayer((layer) => {
      const isMapLayer =
        layer.options?.pane !== LEAFLET_PANES.fields && layer.options?.pane !== 'overlayPane';
      if (isMapLayer) {
        if (layer.options.SATFARM_INTERNAL_ID === mapLayer) {
          leafletMap.current.getPane(layer.options.pane).style.zIndex = 0;
        } else {
          leafletMap.current.getPane(layer.options.pane).style.zIndex = -1;
        }
      }
    });
  }, [mapLayer]);

  return (
    <Map ref={(el) => (mapRef.current = el)}>
      {hasDraggedMap && (
        <SearchNewFieldsPanel>
          {fieldsStatus === FETCH_STATUSES.loading ? (
            <img style={{ width: 32, color: 'white' }} src={Spinner} alt="" />
          ) : zoom < MIN_ZOOM_LEVEL_TO_SEARCH_FOR_FIELDS ? (
            <span className="zoom-in-alert">Please zoom in to search for fields</span>
          ) : (
            <ButtonPrimarySmall onClick={() => onSearchClick(leafletMap.current.getBounds())}>
              {t`Search for fields`}
            </ButtonPrimarySmall>
          )}
        </SearchNewFieldsPanel>
      )}
      <MapFilterPanel>
        <Filter
          popupDirection="right"
          shouldDisplaySaveChangesButton={false}
          onOutsideClick={onOutsideFilterClick}
          onToggleClick={onToggleFilterClick}
          onCloseClick={onFilterCloseClick}
          isOpen={activeFilter === FILTERS.mapLayer}
          label={
            <>
              {t`Map settings`}
              <FaCog className="map-settings-icon" />
            </>
          }
        >
          {MAP_LAYERS.map((layer, index) => (
            <MapLayerCard
              key={layer.settings.SATFARM_INTERNAL_ID}
              id={layer.settings.SATFARM_INTERNAL_ID}
              index={index}
              onClick={() => onFilterChangeClick(layer.settings.SATFARM_INTERNAL_ID)}
              name={layer.name}
              image={layer.image}
              checked={mapLayer === layer.settings.SATFARM_INTERNAL_ID}
            />
          ))}
        </Filter>
      </MapFilterPanel>
    </Map>
  );
}
